// TodoListApp.jsx
import './style.css';
import React, { useState, useEffect, useMemo, useRef } from 'react'; // useRef hinzugefügt
import TodoForm from './components/TodoForm';
import TodoItem from './components/TodoItem';
import { useTodos } from './hooks/useTodos'; 
import { sortTodos } from './utils/sortUtils';

// Helferfunktion für Gruppierung nach Datum
const groupTodosByDate = (todos) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const groups = {
        today: [],
        last7Days: [],
        last30Days: [],
        olderThan30Days: [],
    };

    todos.forEach(todo => {
        const todoDate = new Date(todo.createdAt); 
        todoDate.setHours(0, 0, 0, 0);

        const oneDay = 1000 * 60 * 60 * 24;
        const diffInTime = now.getTime() - todoDate.getTime();
        const diffInDays = Math.floor(diffInTime / oneDay);

        if (diffInDays < 0) {
            groups.today.push(todo); 
        } else if (diffInDays === 0) {
            groups.today.push(todo);
        } else if (diffInDays <= 7) {
            groups.last7Days.push(todo);
        } else if (diffInDays <= 30) {
            groups.last30Days.push(todo);
        } else {
            groups.olderThan30Days.push(todo);
        }
    });

    return groups;
};


function TodoListApp() {
  const [userMessage, setUserMessage] = useState(null);
  const [displayMode, setDisplayMode] = useState('priority');
  const [collapsedHigh, setCollapsedHigh] = useState(false);
  const [collapsedNormal, setCollapsedNormal] = useState(false);
  const [collapsedLow, setCollapsedLow] = useState(false);
  const [collapsedToday, setCollapsedToday] = useState(false);
  const [collapsedLast7Days, setCollapsedLast7Days] = useState(false);
  const [collapsedLast30Days, setCollapsedLast30Days] = useState(false);
  const [collapsedOlderThan30Days, setCollapsedOlderThan30Days] = useState(false);
  const [collapsedCompleted, setCollapsedCompleted] = useState(true);
  const [showStats, setShowStats] = useState(true);

  // NEU: States für den Button unten rechts
  const [showFab, setShowFab] = useState(false);
  const inputRef = useRef(null);

  const sortCriterionForHook = 
    displayMode === 'grouped' || displayMode === 'groupedDate' ? 'priority' : displayMode; 

  const { 
      todos, 
      loading, 
      error, 
      handleAddTask: hookHandleAddTask, 
      handleDeleteTask: hookHandleDeleteTask, 
      handleToggleCompleted: hookHandleToggleCompleted, 
      handleUpdateTask: hookHandleUpdateTask, 
  } = useTodos(sortCriterionForHook); 
  
  // NEU: Effekt zur Überwachung der Scroll-Position
  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = document.documentElement.scrollTop;
      const clientHeight = document.documentElement.clientHeight;

      // Button einblenden, wenn man am Ende der Liste angekommen ist (Skizze)
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        setShowFab(true);
      } else {
        setShowFab(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
      let message;
      if (displayMode === 'grouped') {
          message = 'Ansicht: Gruppiert nach Priorität und Status.'; 
      } else if (displayMode === 'groupedDate') { 
          message = 'Ansicht: Gruppiert nach Erstellungsdatum (Historisch).';
      } else if (displayMode === 'priority') {
          message = 'Sortiert nach Priorität (Offen/Erledigt getrennt).';
      } else if (displayMode === 'createdAt') {
          message = 'Sortiert nach Erstellungsdatum (Neueste zuerst).';
      } else {
          message = 'Sortierung aktualisiert.'; 
      }
      displayUserMessage(message, 2000);
  }, [displayMode]); 
  
  const displayUserMessage = (msg, duration = 3000) => {
    setUserMessage(msg);
    setTimeout(() => setUserMessage(null), duration);
  };

  const handleAddTask = async (text, priority, category) => {
    try {
      await hookHandleAddTask(text, priority, category);
      displayUserMessage('Aufgabe erfolgreich hinzugefügt.', 1500);
    } catch (err) {
      displayUserMessage(err.message, 5000);
    }
  };

  // NEU: Funktion für den Klick auf den blauen FAB
  const handleFabClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Kurze Verzögerung für den Fokus, bis Scrollvorgang gestartet ist
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 400);
  };

  const handleToggleCompleted = async (id, currentStatus) => {
    try {
      await hookHandleToggleCompleted(id, currentStatus);
    } catch (err) {
      displayUserMessage(err.message, 5000);
    }
  };
  
  const handleUpdateTask = async (id, updatedData) => {
    try {
      await hookHandleUpdateTask(id, updatedData);
      displayUserMessage('Aufgabe erfolgreich aktualisiert.', 1500);
    } catch (err) {
      displayUserMessage(err.message, 5000);
    }
  };

  const handleDeleteTask = async (id) => {
    try {
      await hookHandleDeleteTask(id);
      displayUserMessage('Aufgabe gelöscht.', 1500);
    } catch (err) {
      displayUserMessage(err.message, 5000);
    }
  };

  const { completedTodos, openTodos, groupedOpenTodos, groupedOpenTodosByDate } = useMemo(() => {
      const open = todos.filter(t => !t.completed);
      const completed = todos.filter(t => t.completed);

      const groupsByPriority = { High: [], Normal: [], Low: [] };
      open.forEach(todo => {
          const priority = todo.priority || 'Normal';
          if (groupsByPriority[priority]) {
              groupsByPriority[priority].push(todo);
          } else {
              groupsByPriority['Normal'].push(todo);
          }
      });

      const groupsByDate = groupTodosByDate(open); 
      
      return { 
          completedTodos: completed,
          openTodos: open,
          groupedOpenTodos: groupsByPriority, 
          groupedOpenTodosByDate: groupsByDate 
      };
  }, [todos]);

  const stats = {
      total: todos.length,
      completed: todos.filter(t => t.completed).length, 
      open: todos.filter(t => !t.completed).length
  };

  if (loading) return <p>Lade Aufgaben...</p>;
  if (error) return <p style={{ color: 'red' }}>Fehler: {error}</p>; 

  const PriorityGroup = ({ title, todos, isCollapsed, toggleCollapsed }) => (
      <div className="priority-group-container" style={{ marginBottom: '20px' }}>
          <h2 
              onClick={toggleCollapsed} 
              style={{ 
                  cursor: 'pointer', 
                  backgroundColor: '#f8f9fa', 
                  padding: '10px 15px', 
                  borderRadius: '5px', 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '10px',
                  color: '#495057'
              }}
          >
              <span>{title} ({todos.length})</span> 
              <span>{isCollapsed ? '▶' : '▼'}</span>
          </h2>
          
          {!isCollapsed && (
              <ul style={{ listStyle: 'none', padding: 0 }}>
                  {todos.map((todo) => (
                      <li
                          key={String(todo._id)} 
                          style={{
                              marginBottom: '10px',
                              borderRadius: '6px',
                          }}
                      >
                          <TodoItem
                              todo={todo}
                              onDelete={handleDeleteTask}
                              onToggleCompleted={handleToggleCompleted}
                              onUpdateTask={handleUpdateTask} 
                          />
                      </li>
                  ))}
              </ul>
          )}
      </div>
  );

  return (
    <div className={`todo-list`} style={{ maxWidth: '600px', margin: '0 auto', paddingBottom: '100px', position: 'relative' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', color: '#333' }}>
        <h1>Task Mate</h1>
      </header>

      {/* Statistikbereich */}
      <div 
        className="todo-statistics" 
        style={{ 
          marginBottom: '1rem', 
          padding: '10px', 
          border: '1px solid #ccc', 
          borderRadius: '8px', 
          backgroundColor: '#f9f9f9',
          color: '#222'
        }}
      >
        <h3 
          onClick={() => setShowStats(!showStats)} 
          style={{ 
            marginTop: 0, 
            marginBottom: showStats ? '10px' : 0, 
            cursor: 'pointer', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center'
          }}
        >
          Aufgaben-Statistik
          <span>{showStats ? '▲' : '▼'}</span>
        </h3>

        {showStats && (
          <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap' }}>
            <span>Gesamt: {stats.total}</span>
            <span>Offen: {stats.open}</span>
            <span>Abgeschlossen: {stats.completed}</span>
          </div>
        )}
      </div>

      {/* TodoForm mit Übergabe der inputRef */}
      <TodoForm onAddTask={handleAddTask} inputRef={inputRef} />

      {userMessage && (
        <div style={{
          position: 'fixed', bottom: '25px', right: '25px',
          backgroundColor: userMessage.startsWith('Fehler:') ? 'rgba(255, 77, 79, 0.95)' : 'rgba(82, 196, 26, 0.95)',
          color: '#fff', padding: '12px 20px', borderRadius: '10px', zIndex: 9999
        }}>
          {userMessage}
        </div>
      )}
      
      <div className="todo-list-header"> 
          <h2>Alle Aufgaben ({todos.length})</h2> 
          <div className="sort-controls">
              <label htmlFor="sort-criterion">Ansicht/Sortierung:</label>
              <select 
                  id="sort-criterion"
                  value={displayMode}
                  onChange={(e) => setDisplayMode(e.target.value)}
                  className="sort-select" 
              >
                  <option value="priority">Sortieren: Priorität (High zuerst)</option>
                  <option value="createdAt">Sortieren: Erstellungsdatum (Neueste zuerst)</option>
                  <option value="grouped">Gruppieren nach Priorität</option>
                  <option value="groupedDate">Gruppieren nach Datum</option>
              </select>
          </div>
      </div>

      {displayMode === 'grouped' ? (
          <div className="prioritized-todo-list">
              <PriorityGroup
                  title={`High Priorität`} 
                  todos={groupedOpenTodos.High}
                  isCollapsed={collapsedHigh}
                  toggleCollapsed={() => setCollapsedHigh(prev => !prev)}
              />
              <PriorityGroup
                  title={`Normal Priorität`}
                  todos={groupedOpenTodos.Normal}
                  isCollapsed={collapsedNormal}
                  toggleCollapsed={() => setCollapsedNormal(prev => !prev)}
              />
              <PriorityGroup
                  title={`Low Priorität`}
                  todos={groupedOpenTodos.Low}
                  isCollapsed={collapsedLow}
                  toggleCollapsed={() => setCollapsedLow(prev => !prev)}
              />
              {completedTodos.length > 0 && (
                  <PriorityGroup
                      title={`Erledigte Aufgaben`} 
                      todos={completedTodos}
                      isCollapsed={collapsedCompleted}
                      toggleCollapsed={() => setCollapsedCompleted(prev => !prev)}
                  />
              )}
          </div>
      ) : displayMode === 'groupedDate' ? (
          <div className="date-prioritized-todo-list">
              <PriorityGroup title={`Heute`} todos={groupedOpenTodosByDate.today} isCollapsed={collapsedToday} toggleCollapsed={() => setCollapsedToday(prev => !prev)} />
              <PriorityGroup title={`Letzte 7 Tage`} todos={groupedOpenTodosByDate.last7Days} isCollapsed={collapsedLast7Days} toggleCollapsed={() => setCollapsedLast7Days(prev => !prev)} />
              <PriorityGroup title={`Letzte 30 Tage`} todos={groupedOpenTodosByDate.last30Days} isCollapsed={collapsedLast30Days} toggleCollapsed={() => setCollapsedLast30Days(prev => !prev)} />
              <PriorityGroup title={`Vor über 30 Tagen`} todos={groupedOpenTodosByDate.olderThan30Days} isCollapsed={collapsedOlderThan30Days} toggleCollapsed={() => setCollapsedOlderThan30Days(prev => !prev)} />
              {completedTodos.length > 0 && (
                  <PriorityGroup title={`Erledigte Aufgaben`} todos={completedTodos} isCollapsed={collapsedCompleted} toggleCollapsed={() => setCollapsedCompleted(prev => !prev)} />
              )}
          </div>
      ) : displayMode === 'priority' ? (
          <>
              <h3>Offene Aufgaben (Sortiert nach Priorität)</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                  {sortTodos(openTodos, 'priority').map((todo) => (
                      <li key={String(todo._id)} style={{ marginBottom: '10px', borderRadius: '6px' }}>
                          <TodoItem todo={todo} onDelete={handleDeleteTask} onToggleCompleted={handleToggleCompleted} onUpdateTask={handleUpdateTask} />
                      </li>
                  ))}
              </ul>
              {completedTodos.length > 0 && (
                  <>
                      <h3 style={{ marginTop: '30px' }}>✅ Erledigte Aufgaben ({completedTodos.length})</h3>
                      <ul style={{ listStyle: 'none', padding: 0 }}>
                          {completedTodos.map((todo) => (
                              <li key={String(todo._id)} style={{ marginBottom: '10px', borderRadius: '6px' }}>
                                  <TodoItem todo={todo} onDelete={handleDeleteTask} onToggleCompleted={handleToggleCompleted} onUpdateTask={handleUpdateTask} />
                              </li>
                          ))}
                      </ul>
                  </>
              )}
          </>
      ) : (
          <ul style={{ listStyle: 'none', padding: 0 }}>
              {todos.map((todo) => (
                  <li key={String(todo._id)} style={{ marginBottom: '10px', borderRadius: '6px' }}>
                      <TodoItem todo={todo} onDelete={handleDeleteTask} onToggleCompleted={handleToggleCompleted} onUpdateTask={hookHandleUpdateTask} />
                  </li>
              ))}
          </ul>
      )}

      {/* DER NEUE BUTTON (FAB) */}
      {showFab && (
        <button
          onClick={handleFabClick}
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            fontSize: '32px',
            cursor: 'pointer',
            boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            transition: 'opacity 0.3s'
          }}
        >
          +
        </button>
      )}
    </div>
  );
}

export default TodoListApp;